import { PaletteMode } from '@mui/material';
import { ThemeOptions } from '@mui/material/styles';

import { getAppBrandConfigs } from './brandConfig';

export const getDesignPalette = (mode: PaletteMode): ThemeOptions => {
  const colors = getAppBrandConfigs(mode);

  return {
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: {
              main: colors.primary, // '#3B6EB7',
            },
            text: {
              primary: '#000',
              secondary: '#6c757d',
            },
            secondary: {
              main: colors.secondary, // '#E6F3FC',
              contrastText: colors.primary, // '#3B6EB7'
            },
          }
        : {
            primary: {
              main: colors.primary, // '#9DCFF5',
            },
            text: {
              primary: '#fff',
              secondary: 'rgba(255, 255, 255, 0.6)',
            },
            secondary: {
              main: colors.secondary, // '#9DCFF5',
              contrastText: colors.primary, // '#9DCFF5'
            },
            background: {
              paper: '#141519',
            },
          }),
    },
  };
};

export const getDesignComponents = (mode: PaletteMode): ThemeOptions => {
  const colors = getAppBrandConfigs(mode);

  return {
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.875rem',
            color: 'inherit',
            lineHeight: '2rem',
          },
        },
      },
      ...(mode === 'light'
        ? {
            MuiButton: {
              defaultProps: {
                variant: 'contained',
                color: 'primary',
              },
              styleOverrides: {
                disableElevation: true,
                root: {
                  textTransform: 'capitalize',
                  borderRadius: 8,
                  boxShadow: 'none',
                  '&.Mui-disabled': {
                    color: '#fff',
                    backgroundColor: '#C2C9D1',
                  },
                  '&.MuiButton-containedPrimary:hover': {
                    background: colors.primaryHover || colors.primary, // '#4A91DC',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedSecondary:hover': {
                    background: colors.secondaryHover || colors.secondary, // '#C1E1F9',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedError': {
                    color: '#E74536',
                    background: '#FBF5F4',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedInfo': {
                    color: '#000',
                    background: 'rgba(0, 0, 0, 0.05)',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-sizeSmall': {
                    svg: {
                      width: '20px',
                    },
                  },
                },
              },
            },
            MuiButtonGroup: {
              styleOverrides: {
                root: {
                  borderRadius: 8,
                  boxShadow: 'none',
                },
              },
            },
            MuiIconButton: {
              styleOverrides: {
                root: {
                  '&.MuiIconButton-colorInfo': {
                    color: '#9DCFF5',
                  },
                },
              },
            },
            MuiChip: {
              styleOverrides: {
                root: {
                  '&.MuiChip-colorDefault': {
                    backgroundColor: 'transparent',
                  },
                  '&.MuiChip-colorWarning': {
                    backgroundColor: '#f1c40f',
                  },
                  '&.MuiChip-colorInfo': {
                    color: '#192128',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  },
                },
              },
            },
            MuiTableCell: {
              styleOverrides: {
                root: ({ ownerState }) => ({
                  background: ownerState.variant === 'head' ? '#F1F3F9' : undefined,
                  fontSize: ownerState.variant === 'head' ? '0.75rem' : undefined,
                }),
              },
            },
            MuiTooltip: {
              styleOverrides: {
                tooltip: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: colors.primary, // '#3B6EB7',
                  backgroundColor: colors.secondary, // '#E6F3FC',
                },
                arrow: {
                  color: colors.secondary, // '#E6F3FC',
                },
              },
            },
            MuiPaper: {
              defaultProps: {
                elevation: 0,
                variant: 'outlined',
              },
            },
            MuiToggleButton: {
              styleOverrides: {
                root: {
                  '&.Mui-selected': {
                    color: '#fff',
                    background: colors.primary, // '#3B6EB7',
                  },
                },
              },
            },
            MuiInputBase: {
              styleOverrides: {
                root: {
                  borderRadius: '8px !important',
                  background: '#fff',
                  '&:has(.Mui-disabled)': {
                    background: 'rgba(0, 0, 0, 0.05)',
                  },
                },
                input: {
                  height: '40px',
                },
              },
            },
          }
        : {
            MuiButton: {
              defaultProps: {
                variant: 'outlined',
                color: 'primary',
              },
              styleOverrides: {
                disableElevation: true,
                root: {
                  textTransform: 'capitalize',
                  borderRadius: 8,
                  boxShadow: 'none',
                  '&.MuiButton-outlinedSecondary': {
                    color: colors.secondary, // '#9DCFF5',
                  },
                  '&.MuiButton-outlinedSecondary:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-disabled': {
                    color: '#fff',
                    backgroundColor: 'rgba(255, 255, 255, 0.38)',
                  },
                  '&.MuiButton-containedPrimary:hover': {
                    background: '#7ABBF0',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedSecondary': {
                    color: colors.secondary, // '#9DCFF5',
                    border: '1px solid ' + colors.secondary, // '#9DCFF5',
                    background: 'transparent',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedSecondary:hover': {
                    color: '#9DCFF5',
                    background: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedError': {
                    color: '#E74536',
                    background: '#FBF5F4',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-containedInfo': {
                    color: '#fff',
                    background: '#47484D',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-sizeSmall': {
                    svg: {
                      width: '20px',
                    },
                  },
                },
              },
            },
            MuiButtonGroup: {
              styleOverrides: {
                root: {
                  borderRadius: 8,
                  boxShadow: 'none',
                },
              },
            },
            MuiIconButton: {
              styleOverrides: {
                root: {
                  '&.MuiIconButton-colorInfo': {
                    color: 'rgba(255, 255, 255, 0.6)',
                  },
                },
              },
            },
            MuiChip: {
              styleOverrides: {
                root: {
                  '&.MuiChip-colorDefault': {
                    backgroundColor: 'transparent',
                  },
                  '&.MuiChip-colorWarning': {
                    backgroundColor: '#f1c40f',
                  },
                  '&.MuiChip-colorInfo': {
                    color: '#192128',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  },
                },
              },
            },
            MuiTooltip: {
              styleOverrides: {
                tooltip: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#fff',
                  backgroundColor: '#47484D',
                },
                arrow: {
                  color: '#47484D',
                },
              },
            },
            MuiDrawer: {
              defaultProps: {
                elevation: 1,
              },
            },
            MuiToggleButton: {
              styleOverrides: {
                root: {
                  '&.Mui-selected': {
                    color: '#000',
                    background: colors.secondary, // '#9DCFF5',
                  },
                },
              },
            },
            MuiTextField: {
              styleOverrides: {
                root: {
                  '.MuiInputBase-root': {
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                },
              },
            },
            MuiFormControl: {
              styleOverrides: {
                root: {
                  '.MuiInputBase-root': {
                    color: '#fff',
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                },
              },
            },
            MuiInputBase: {
              styleOverrides: {
                root: {
                  borderRadius: '8px !important',
                  background: 'rgba(255, 255, 255, 0.08)',
                  '&:has(.Mui-disabled)': {
                    background: 'rgba(255, 255, 255, 0.5)',
                  },
                },
                input: {
                  height: '40px',
                },
              },
            },
          }),
    },
  };
};
